import styles from './ReportData.module.css';
import { Image } from '@round/ui-kit';
import HeaderSummary from 'Modules/Plans/components/HeaderSummary/HeaderSummary';
import CircleBadge from 'Modules/Plans/components/CircleBadge/CircleBadge';
import { getUserNameInitials } from 'Modules/Plans/User/user.helpers';
import RunDates from 'Modules/Plans/components/RunDates/RunDates';
import moment from 'moment';
import { asMoneyWithoutZeros } from 'helpers';
import { OptionsContext } from 'contexts/OptionsContext/OptionsContext';
import useNonNullContext from 'Hooks/useNonNullContext';
import { AutoGrowthInput } from '@round/whitelabel-ui';
import { useEffect, useState } from 'react';
import { creatorbase, creatorbaseQueries } from '@round/api';
import { keepPreviousData } from '@tanstack/react-query';
import { showNotification } from 'helpers';
import OverflowCircleBadgeContainer from 'Modules/Plans/components/OverflowCircleBadgeContainer/OverflowCircleBadgeContainer';
import cn from 'classnames';
import { Button } from '@round/whitelabel-ui';
import useCurrentUserClients from '../../../Clients/hooks/useCurrentUserClients';
import ShareDropdown from './ShareDropdown/ShareDropdown';
import {
    useCreatePublicReport,
    useDeletePublicReport,
    useReport,
    useTeams,
    useUpdatePublicReport,
    useUpdateReportMutation,
    useUsers,
} from '@round/creatorbase-helpers/api-hooks';
import useClientSelect from 'Modules/Plans/Clients/hooks/useClientSelect';
import TeamNameCard from 'Modules/Plans/Team/components/TeamNameCard/TeamNameCard';
import { ImageUpload } from '@round/whitelabel-ui';
import useReportId from '../hooks/useReportId';

type Props = {
    onClickEditReport: () => void;
    className?: string;
};

const ReportData = ({ className, onClickEditReport }: Props) => {
    const reportId = useReportId();
    const [reportName, setReportName] = useState('');
    const [reportDescription, setReportDescription] = useState('');

    const {
        data,
        isLoading: isReportLoading,
        refetch: refetchReport,
    } = useReport(reportId, { placeholderData: keepPreviousData });
    const {
        update: updateReport,
        isLoading: isUpdatePending,
        variables: updateVariables,
    } = useUpdateReportMutation(reportId);
    const isInitialized = !isReportLoading;

    const { createAsync: postPublicReport } = useCreatePublicReport();
    const { updateAsync: patchPublicReport } = useUpdatePublicReport();
    const { deleteOneAsync: deletePublicReport } = useDeletePublicReport();

    // Resolve teams and planners from the report's own id sets in a single request each.
    const teamIds = data?.teams_assigned_to_campaigns ?? [];
    const { data: teamsResponse, isLoading: isTeamsLoading } = useTeams(
        { id: teamIds.join(','), page_size: teamIds.length },
        { enabled: teamIds.length > 0 }
    );
    const teams = teamsResponse?.results ?? [];

    const assigneeIds = data?.users_assigned_to_campaigns ?? [];
    const { data: assigneesResponse, isLoading: isAssigneesLoading } = useUsers(
        { id: assigneeIds.join(','), page_size: assigneeIds.length },
        { enabled: assigneeIds.length > 0 }
    );
    const assignees = assigneesResponse?.results ?? [];
    const { currencies } = useNonNullContext(OptionsContext);

    const handleCreateLinkSuccess = () => {
        refetchReport();
    };

    const cost = asMoneyWithoutZeros(
        data?.total_cost,
        currencies.find((c) => c.id === data?.currency_id)
    );

    // Seed the editable name/description fields from the report once it loads (and after refetches).
    useEffect(() => {
        if (data) {
            setReportName(data.name);
            setReportDescription(data.description);
        }
    }, [data]);

    const fieldsUpdating = isUpdatePending ? Object.keys(updateVariables ?? {}) : [];
    const handleReportUpdate = async (payload: Partial<creatorbase.PatchReportData>) => {
        if (!reportId) {
            return;
        }

        try {
            const updated = await updateReport({ id: reportId, ...payload });
            setReportName(updated.name);
            setReportDescription(updated.description);
            showNotification('Report updated', 'info');
        } catch (error) {
            setReportName(data?.name || '-');
            setReportDescription(data?.description || '-');
            showNotification(
                error instanceof Error
                    ? creatorbaseQueries.reports.utils.update.getErrorMessage(error)
                    : 'Could not update report',
                'error'
            );
        }
    };

    const handlePublishReport = async (payload: creatorbase.PostPublicReportData) => {
        try {
            await postPublicReport(payload);
            showNotification('Shareable link created', 'info');
        } catch (error) {
            showNotification(
                error instanceof Error
                    ? creatorbaseQueries.publicReport.utils.create.getErrorMessage(error)
                    : 'Could not create shareable link',
                'error'
            );
        }
    };

    const handleDeleteReport = async (publicReportId: string | null | undefined) => {
        if (!publicReportId) {
            showNotification('Could not unpublish shareable link', 'error');
            return;
        }

        try {
            await deletePublicReport(publicReportId);
            showNotification('Shareable link deleted', 'info');
            // Refresh the report so its public_reports list reflects the deletion.
            handleCreateLinkSuccess();
        } catch (error) {
            showNotification(
                error instanceof Error
                    ? creatorbaseQueries.publicReport.utils.delete.getErrorMessage(error)
                    : 'Could not delete shareable link',
                'error'
            );
        }
    };

    const handleUpdatePublicReport = async (updatedReport: creatorbase.PublicReportsListItem) => {
        try {
            await patchPublicReport({
                id: updatedReport.id,
                include_cost: updatedReport.include_cost,
                include_benchmark: updatedReport.include_benchmark,
                include_ad_codes: updatedReport.include_ad_codes,
                name: updatedReport.name,
                active: updatedReport.active,
            });
            showNotification('Shareable link updated successfully', 'info');
            // Refresh the report so its public_reports list reflects the update.
            handleCreateLinkSuccess();
        } catch (error) {
            showNotification(
                error instanceof Error
                    ? creatorbaseQueries.publicReport.utils.update.getErrorMessage(error)
                    : 'Failed to update shareable link',
                'error'
            );
        }
    };

    const { clients } = useCurrentUserClients();
    const { selectedClientId, setSelectedClientId } = useClientSelect();
    useEffect(() => {
        const [team] = teamsResponse?.results ?? [];
        if (team?.client_id && team.client_id !== selectedClientId) {
            setSelectedClientId(team.client_id);
        }
    }, [selectedClientId, setSelectedClientId, teamsResponse]);

    return (
        <div className={styles.reportInfoContainer}>
            <ImageUpload
                className={styles.reportImage}
                isLoading={fieldsUpdating.includes('image')}
                value={data?.image}
                onUpload={(file) => handleReportUpdate({ image: file })}
            />

            <div className={cn(styles.reportDetails, className)}>
                <div className={styles.titleSection}>
                    <div className={styles.titles}>
                        <AutoGrowthInput
                            containerClassName={styles.title}
                            isLoading={!isInitialized}
                            type="text"
                            value={reportName}
                            onChange={(e) => setReportName(e.target.value)}
                            onBlur={() => {
                                if (!data || reportName === data.name) {
                                    return;
                                }

                                handleReportUpdate({ name: reportName });
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.currentTarget.blur();
                                }
                            }}
                        />

                        <AutoGrowthInput
                            containerClassName={styles.description}
                            isLoading={!isInitialized}
                            type="text"
                            value={reportDescription}
                            onChange={(e) => setReportDescription(e.target.value)}
                            onBlur={() => {
                                if (!data || reportDescription === data.description) {
                                    return;
                                }

                                handleReportUpdate({ description: reportDescription });
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.currentTarget.blur();
                                }
                            }}
                        />
                    </div>
                    <div className={styles.publishActions}>
                        {isInitialized && (
                            <>
                                <Button
                                    appearance="primary"
                                    className={styles.editButton}
                                    onClick={() => onClickEditReport()}
                                >
                                    Edit Report
                                </Button>

                                <ShareDropdown
                                    isInitialized={isInitialized}
                                    onCreateLink={handleCreateLinkSuccess}
                                    publicReports={data?.public_reports || []}
                                    onDeleteReport={handleDeleteReport}
                                    onUpdateReport={handleUpdatePublicReport}
                                    reportId={data?.id || ''}
                                    onPublishReport={handlePublishReport}
                                />
                            </>
                        )}
                    </div>
                </div>

                <HeaderSummary>
                    <HeaderSummary.Item>
                        <HeaderSummary.Label>Run Dates</HeaderSummary.Label>
                        <HeaderSummary.Value isLoading={!isInitialized}>
                            {data?.first_post_date && data?.last_post_date ? (
                                <RunDates
                                    startDate={moment(data.first_post_date).format('DD MMM YYYY')}
                                    endDate={moment(data.last_post_date).format('DD MMM YYYY')}
                                />
                            ) : (
                                '-'
                            )}
                        </HeaderSummary.Value>
                    </HeaderSummary.Item>

                    <HeaderSummary.Item>
                        <HeaderSummary.Label>Total cost</HeaderSummary.Label>
                        <HeaderSummary.Value isLoading={!isInitialized}>{cost}</HeaderSummary.Value>
                    </HeaderSummary.Item>

                    <HeaderSummary.Item>
                        <HeaderSummary.Label>Teams</HeaderSummary.Label>
                        <HeaderSummary.Value>
                            {data?.teams_assigned_to_campaigns?.length ? (
                                <OverflowCircleBadgeContainer
                                    isLoading={isTeamsLoading}
                                    className={styles.badgeContainer}
                                    skeletonClassName={styles.badge}
                                    overflowingCountBadgeClassName={styles.badge}
                                >
                                    {data?.teams_assigned_to_campaigns?.map((id) => {
                                        const team = teams.find((team) => team.id === id);
                                        if (!team) {
                                            return null;
                                        }

                                        const client = clients.find((c) => c.id === team.client_id);
                                        return (
                                            <CircleBadge
                                                className={cn(styles.badge, styles.teamBadge)}
                                                key={team.id}
                                                tooltip={<TeamNameCard team={team} client={client} />}
                                            >
                                                <Image src={client?.logo || ''} className={styles.clientImage} />

                                                <Image
                                                    skeletonCircle
                                                    className={styles.teamImage}
                                                    src={team.logo || ''}
                                                />
                                            </CircleBadge>
                                        );
                                    })}
                                </OverflowCircleBadgeContainer>
                            ) : (
                                '-'
                            )}
                        </HeaderSummary.Value>
                    </HeaderSummary.Item>

                    <HeaderSummary.Item>
                        <HeaderSummary.Label>Planners</HeaderSummary.Label>
                        <HeaderSummary.Value className={styles.badgeContainer}>
                            {data?.users_assigned_to_campaigns?.length ? (
                                <OverflowCircleBadgeContainer
                                    isLoading={isAssigneesLoading}
                                    className={styles.badgeContainer}
                                    skeletonClassName={styles.badge}
                                    overflowingCountBadgeClassName={styles.badge}
                                >
                                    {data.users_assigned_to_campaigns?.map((id) => {
                                        const assignee = assignees.find((assignee) => assignee.id === id);
                                        if (!assignee) {
                                            return null;
                                        }
                                        return (
                                            <CircleBadge
                                                className={styles.badge}
                                                key={assignee.id}
                                                tooltip={assignee.name}
                                            >
                                                {getUserNameInitials(assignee.name)}
                                            </CircleBadge>
                                        );
                                    })}
                                </OverflowCircleBadgeContainer>
                            ) : (
                                '-'
                            )}
                        </HeaderSummary.Value>
                    </HeaderSummary.Item>
                </HeaderSummary>
            </div>
        </div>
    );
};

export default ReportData;
